import { useId, useState } from 'react';
import { CURRENCIES, LANGUAGES } from '../../lib/settingsStore.js';
import AddressAutocomplete from '../AddressAutocomplete.jsx';
import FieldTip from '../FieldTip.jsx';
import {
  coerceDebtorCountry,
  debtorCountryLabel,
} from '../../lib/debtorCountry.js';
import { normalizePostalCode } from '../../lib/postalCode.js';
import {
  PROFILE_AVATAR_ACCEPT,
  fileToAvatarDataUrl,
} from '../../lib/profileAvatar.js';

const PROFILE_CURRENCY_TIP =
  'Default currency for new demand amounts. Change it on a demand when collecting in another country’s money. Separate from DemandJet billing fees.';
const PROFILE_EMAIL_TIP = 'We’ll use this email address for notifications.';
const PROFILE_PHONE_TIP = 'We’ll use this phone number for notifications.';

function initialsFromProfile(user, form) {
  const name = String(form?.displayName || user?.displayName || user?.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = String(user?.email || '').trim();
  if (email) return email.slice(0, 2).toUpperCase();
  return 'DJ';
}

export default function ProfilePanel({
  user,
  form,
  onChange,
  onSubmit,
  pending,
}) {
  const isAttorney = user?.role === 'ATTORNEY' || user?.role === 'ADMIN';
  const fileInputId = useId();
  const [photoError, setPhotoError] = useState('');
  const [photoPending, setPhotoPending] = useState(false);
  const initials = initialsFromProfile(user, form);
  const photo = String(form?.avatarDataUrl || '').trim();

  async function onPickPhoto(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoError('');
    setPhotoPending(true);
    try {
      const avatarDataUrl = await fileToAvatarDataUrl(file);
      onChange({ avatarDataUrl });
    } catch (err) {
      setPhotoError(err?.message || 'Could not use that photo.');
    } finally {
      setPhotoPending(false);
    }
  }

  function removePhoto() {
    setPhotoError('');
    onChange({ avatarDataUrl: '' });
  }

  return (
    <form className="settings-form" onSubmit={onSubmit}>
      <h2>Profile</h2>
      <p className="settings-lead">
        Customer identity and organization details used across DemandJet.
      </p>

      <div className="profile-photo-block">
        <div className="profile-photo-preview" aria-hidden={photo ? undefined : true}>
          {photo ? (
            <img src={photo} alt="" className="profile-photo-image" />
          ) : (
            <span className="profile-photo-initials">{initials}</span>
          )}
        </div>
        <div className="profile-photo-actions">
          <p className="profile-photo-label">Profile photo</p>
          <p className="settings-lead profile-photo-hint">JPG, PNG, WebP, or GIF.</p>
          <div className="profile-photo-buttons">
            <input
              id={fileInputId}
              type="file"
              accept={PROFILE_AVATAR_ACCEPT}
              className="profile-photo-input"
              onChange={onPickPhoto}
            />
            <label htmlFor={fileInputId} className="btn-ghost profile-photo-upload">
              {photoPending ? 'Processing…' : photo ? 'Change photo' : 'Add photo'}
            </label>
            {photo ? (
              <button type="button" className="btn-ghost" onClick={removePhoto} disabled={pending}>
                Remove
              </button>
            ) : null}
          </div>
          {photoError ? <p className="form-error">{photoError}</p> : null}
        </div>
      </div>

      <div className="settings-grid">
        <label className="field">
          <span>Customer name</span>
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => onChange({ displayName: e.target.value })}
            placeholder="Alex Morgan"
            required
          />
        </label>

        <label className="field">
          <span>Organization name</span>
          <input
            type="text"
            value={form.organizationName}
            onChange={(e) => onChange({ organizationName: e.target.value })}
            placeholder="Acme Collections LLC"
          />
        </label>

        <label className="field">
          <span>Country / Region</span>
          <input
            type="text"
            value={form.countryRegion}
            onChange={(e) => onChange({ countryRegion: e.target.value })}
            placeholder="United States"
          />
        </label>

        <label className="field">
          <span className="field-label-with-tip">
            Currency
            <FieldTip text={PROFILE_CURRENCY_TIP} label="Currency" />
          </span>
          <select
            value={form.currency}
            onChange={(e) => onChange({ currency: e.target.value })}
          >
            {CURRENCIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field field-span-2">
          <span>Street address</span>
          <AddressAutocomplete
            value={form.addressStreet}
            onChange={(next) => onChange({ addressStreet: next })}
            country={form.countryRegion}
            onAddressSelect={(address) => {
              const matched = coerceDebtorCountry(address.country);
              onChange({
                addressStreet: address.street || form.addressStreet,
                addressCity: address.city || form.addressCity,
                addressState: address.state || form.addressState,
                addressPostalCode: address.postalCode || form.addressPostalCode,
                countryRegion: matched
                  ? debtorCountryLabel(matched)
                  : address.country || form.countryRegion,
              });
            }}
            placeholder="123 Main St, Suite 200"
          />
        </label>

        <label className="field">
          <span>City</span>
          <input
            type="text"
            value={form.addressCity}
            onChange={(e) => onChange({ addressCity: e.target.value })}
            placeholder="Austin"
            autoComplete="address-level2"
          />
        </label>

        <label className="field">
          <span>State / Province</span>
          <input
            type="text"
            value={form.addressState}
            onChange={(e) => onChange({ addressState: e.target.value })}
            placeholder="TX"
            autoComplete="address-level1"
          />
        </label>

        <label className="field">
          <span>Postal code</span>
          <input
            type="text"
            value={form.addressPostalCode}
            onChange={(e) => onChange({ addressPostalCode: e.target.value.toUpperCase() })}
            onBlur={() =>
              onChange({ addressPostalCode: normalizePostalCode(form.addressPostalCode) })
            }
            placeholder="78701 or A1A 1A1"
            autoComplete="postal-code"
          />
        </label>

        <label className="field">
          <span>Language</span>
          <select
            value={form.language}
            onChange={(e) => onChange({ language: e.target.value })}
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field-label-with-tip">
            Email address
            <FieldTip text={PROFILE_EMAIL_TIP} label="Email address" />
          </span>
          <input type="email" value={user?.email || ''} disabled />
        </label>

        <label className="field">
          <span className="field-label-with-tip">
            Phone number
            <FieldTip text={PROFILE_PHONE_TIP} label="Phone number" />
          </span>
          <input
            type="tel"
            value={form.phoneNumber}
            onChange={(e) => onChange({ phoneNumber: e.target.value })}
            placeholder="+1 (555) 123-4567"
            autoComplete="tel"
          />
        </label>

        {isAttorney ? (
          <label className="field field-span-2">
            <span>Licensed states</span>
            <input
              type="text"
              value={form.licensedStates}
              onChange={(e) => onChange({ licensedStates: e.target.value.toUpperCase() })}
              placeholder="CA,TX,NY"
            />
            <small className="field-hint">
              Comma-separated codes used for jurisdiction routing.
            </small>
          </label>
        ) : null}
      </div>

      <div className="drawer-actions">
        <button type="submit" className="btn-primary" disabled={pending}>
          {pending ? 'Saving…' : 'Save profile'}
        </button>
      </div>
    </form>
  );
}
